// API client — TypeScript port of src/api.js. Same logic, full types.
// All bug fixes carry forward: hardcoded localhost URLs are removed
// (Vite dev proxies /chat/v2 + /briefings to FastAPI on :8001; prod is
// same-origin so an empty API_BASE works for both).

import type {
  ActivityEntry,
  Agent,
  Artifact,
  Briefing,
  BriefingFilter,
  BriefingSummary,
  Clarifier,
  GraphEditEntry,
  GraphProposal,
  GraphSnapshot,
  GraphStreamEventHandler,
  Message,
  ParsedAttachment,
  Receipt,
  ResearchPlan,
  SavedProductCatalog,
  StreamEvent,
  StreamEventHandler,
  StreamStep,
  Thread,
  TimelineSegment,
  TodoItem,
  User,
} from "./types";
import { appendTextChunk, pushActivity, updateActivity } from "./timeline";

// ─── helpers ──────────────────────────────────────────────────────────
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const uid = () => Math.random().toString(36).slice(2, 10);

// Same-origin in Vite dev (proxy handles it) and in prod (FastAPI serves
// both static + API). Override via VITE_API_BASE if pointing at a remote
// dev backend explicitly.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
const PROXY_URL = API_BASE + "/chat/v2";

// Auth header: SPA stores base64(user:pass) in localStorage after the
// user authenticates via the LoginPage. Every gated API fetch pulls it
// from here. Same key used by the AskUserQuestion picker so that
// /chat/v2/answer carries auth too.
export const AUTH_STORAGE_KEY = "inku.auth";

export function getAuthHeader(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const v = window.localStorage.getItem(AUTH_STORAGE_KEY);
  return v ? { Authorization: `Basic ${v}` } : {};
}

function authHeaders(extra: HeadersInit = {}): Record<string, string> {
  const merged: Record<string, string> = { ...getAuthHeader() };
  if (Array.isArray(extra)) {
    for (const [k, v] of extra) merged[k] = v;
  } else if (extra instanceof Headers) {
    extra.forEach((v, k) => {
      merged[k] = v;
    });
  } else {
    Object.assign(merged, extra);
  }
  return merged;
}

// In-memory File reference cache keyed by attachment id. Populated by
// uploadAndParse() and consumed by chat() so the bytes survive between
// upload and send. Lost on page reload — the parsed metadata in
// localStorage is enough to keep the chat ledger consistent.
const fileCache = new Map<string, File>();

// ─── seeded threads / agents (kept identical to original demo) ────────
const SEEDED_THREADS: Thread[] = [
  {
    id: "t_brame",
    title: "BRAME · Dry January 2026",
    brandId: "b_brame",
    updatedAt: Date.now() - 1000 * 60 * 60 * 2,
    messageCount: 14,
  },
  {
    id: "t_loop",
    title: "Loop Foods · Q2 launch",
    brandId: "b_loop",
    updatedAt: Date.now() - 1000 * 60 * 60 * 30,
    messageCount: 8,
  },
  {
    id: "t_veldt",
    title: "Veldt · bottle drop brief",
    brandId: "b_veldt",
    updatedAt: Date.now() - 1000 * 60 * 60 * 48,
    messageCount: 5,
  },
];

const SEEDED_AGENTS: Agent[] = [
  {
    id: "agent_monday_meta",
    cadence: "Every Monday · 09:00",
    title: "Weekly Meta + TikTok readout",
    desc: "Watches your live creative for fatigue, drafts replacement directions",
    status: "scheduled",
    lastRun: "Today · 09:00",
    unread: true,
  },
  {
    id: "agent_month_kpi",
    cadence: "First of month · 08:00",
    title: "Monthly KPI readout",
    desc: "Pulls Looker + Shopify + Meta, writes the month in plain English",
    status: "scheduled",
    lastRun: "Apr 1 · 08:00",
  },
  {
    id: "agent_q_brand",
    cadence: "Quarterly",
    title: "Brand health pulse",
    desc: "Social listening + sentiment, flags emerging threats",
    status: "paused",
    lastRun: "Q1 2026",
  },
];

// ─── api object ───────────────────────────────────────────────────────
export const api = {
  // ---- AUTH / ME ----
  async me(): Promise<User> {
    await sleep(120);
    return {
      id: "u_bjarne",
      name: "Bjarne",
      org: "Loop Foods",
      orgId: "org_loop",
    };
  },

  // ---- THREADS ----
  async listThreads(): Promise<Thread[]> {
    await sleep(150);
    const raw = localStorage.getItem("inku.threads");
    if (raw) return JSON.parse(raw) as Thread[];
    localStorage.setItem("inku.threads", JSON.stringify(SEEDED_THREADS));
    return SEEDED_THREADS;
  },

  async createThread(
    opts: { title?: string; brandId?: string | null } = {}
  ): Promise<Thread> {
    await sleep(120);
    const thread: Thread = {
      id: "t_" + uid(),
      title: opts.title ?? "Untitled",
      brandId: opts.brandId ?? null,
      updatedAt: Date.now(),
      messageCount: 0,
    };
    const list = await api.listThreads();
    localStorage.setItem("inku.threads", JSON.stringify([thread, ...list]));
    return thread;
  },

  async renameThread(id: string, title: string): Promise<Thread> {
    await sleep(80);
    const list = await api.listThreads();
    const next = list.map((t) =>
      t.id === id ? { ...t, title, updatedAt: Date.now() } : t
    );
    localStorage.setItem("inku.threads", JSON.stringify(next));
    return next.find((t) => t.id === id)!;
  },

  async deleteThread(id: string): Promise<{ ok: true }> {
    await sleep(80);
    const list = await api.listThreads();
    localStorage.setItem(
      "inku.threads",
      JSON.stringify(list.filter((t) => t.id !== id))
    );
    localStorage.removeItem("inku.messages." + id);
    localStorage.removeItem("inku.artifacts." + id);
    return { ok: true };
  },

  // ---- BRIEFINGS (real REST against FastAPI) ----
  async createBriefing(payload: {
    thread_id: string;
    title: string;
    actor: string;
  }): Promise<Briefing> {
    const res = await fetch(API_BASE + "/briefings", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(e.detail || `createBriefing: HTTP ${res.status}`);
    }
    return res.json();
  },

  async listBriefings(
    filter: { status?: string | null; owner?: string | null } = {}
  ): Promise<{ items: BriefingSummary[]; total: number }> {
    const qs = new URLSearchParams();
    if (filter.status) qs.set("status", filter.status);
    if (filter.owner) qs.set("owner", filter.owner);
    const url = API_BASE + "/briefings" + (qs.toString() ? `?${qs}` : "");
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error(`listBriefings: HTTP ${res.status}`);
    return res.json();
  },

  async getBriefing(id: string): Promise<Briefing> {
    const res = await fetch(`${API_BASE}/briefings/${id}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`getBriefing: HTTP ${res.status}`);
    return res.json();
  },

  async patchBriefing(
    id: string,
    patch: Partial<Briefing>
  ): Promise<Briefing> {
    const res = await fetch(`${API_BASE}/briefings/${id}`, {
      method: "PATCH",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(e.detail || `patchBriefing: HTTP ${res.status}`);
    }
    return res.json();
  },

  async transitionBriefing(
    id: string,
    payload: { to: string; actor: string; reason?: string }
  ): Promise<Briefing> {
    const res = await fetch(`${API_BASE}/briefings/${id}/transition`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(e.detail || `transitionBriefing: HTTP ${res.status}`);
    }
    return res.json();
  },

  async commentBriefing(
    id: string,
    payload: { author: string; body: string }
  ): Promise<Briefing> {
    const res = await fetch(`${API_BASE}/briefings/${id}/comments`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(e.detail || `commentBriefing: HTTP ${res.status}`);
    }
    return res.json();
  },

  // Persist a product catalog to the briefing record. Idempotent on
  // catalog.id — calling again with a refined catalog of the same id
  // replaces the previous version.
  async saveBriefingCatalog(
    briefingId: string,
    catalog: SavedProductCatalog,
    actor: string = "user"
  ): Promise<Briefing> {
    const res = await fetch(
      `${API_BASE}/briefings/${briefingId}/catalogs`,
      {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ catalog, actor }),
      }
    );
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(
        e.detail || `saveBriefingCatalog: HTTP ${res.status}`
      );
    }
    return res.json();
  },

  async removeBriefingCatalog(
    briefingId: string,
    catalogId: string
  ): Promise<Briefing> {
    const res = await fetch(
      `${API_BASE}/briefings/${briefingId}/catalogs/${encodeURIComponent(
        catalogId
      )}`,
      {
        method: "DELETE",
        headers: authHeaders(),
      }
    );
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { detail?: string };
      throw new Error(
        e.detail || `removeBriefingCatalog: HTTP ${res.status}`
      );
    }
    return res.json();
  },

  // Wipe ALL persisted client + server state. Dev-only reset.
  async resetAll(): Promise<{ ok: true; server_cleared: number }> {
    let serverCleared = 0;
    try {
      const res = await fetch(API_BASE + "/chat/v2/reset", {
        method: "POST",
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = (await res.json()) as { cleared_sessions?: number };
        serverCleared = data.cleared_sessions ?? 0;
      }
    } catch (e) {
      console.warn("Server reset failed (server may be offline):", e);
    }
    Object.keys(localStorage)
      .filter((k) => k.startsWith("inku."))
      .forEach((k) => localStorage.removeItem(k));
    return { ok: true, server_cleared: serverCleared };
  },

  // ---- REFS (Tigris-backed reference image storage) ----
  // Phase 2 of the image-generation flow. Composer uploads each attached
  // image as soon as it's added so the user gets immediate visual
  // confirmation; the returned id is the stable handle the chat call
  // sends back to the agent.
  async uploadRefs(
    threadId: string,
    files: File[]
  ): Promise<
    {
      /** "image" → existing ref flow (id starts with `ref_`, has `url`).
       *  "data" → CSV/data file (id starts with `data_`, has `columns`
       *  and `row_count` instead of `url`). */
      type?: "image" | "data";
      id: string;
      /** Present for images only. */
      url?: string;
      /** Image ref: original filename. Data: alias for `filename`. */
      name: string;
      size: number;
      mime_type: string;
      /** Data attachments only — present so the composer can show
       *  "n rows" on the thumbnail pill. */
      row_count?: number;
      columns?: string[];
    }[]
  > {
    if (files.length === 0) return [];
    const fd = new FormData();
    for (const f of files) {
      fd.append("files", f, f.name);
    }
    const res = await fetch(
      `${API_BASE}/chat/v2/refs/${encodeURIComponent(threadId)}`,
      {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }
    );
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(
        `Ref upload ${res.status}: ${txt.slice(0, 200) || res.statusText}`
      );
    }
    const data = (await res.json()) as {
      thread_id: string;
      refs: {
        type?: "image" | "data";
        id: string;
        url?: string;
        name: string;
        size: number;
        mime_type: string;
        row_count?: number;
        columns?: string[];
      }[];
    };
    return data.refs;
  },

  // ---- MESSAGES ----
  async listMessages(threadId: string): Promise<Message[]> {
    await sleep(100);
    const raw = localStorage.getItem("inku.messages." + threadId);
    return raw ? (JSON.parse(raw) as Message[]) : [];
  },

  async appendMessage(
    threadId: string,
    message: Omit<Message, "id" | "createdAt">
  ): Promise<Message> {
    await sleep(40);
    const msgs = await api.listMessages(threadId);
    const full: Message = {
      id: "m_" + uid(),
      createdAt: Date.now(),
      ...message,
    };
    localStorage.setItem(
      "inku.messages." + threadId,
      JSON.stringify([...msgs, full])
    );
    return full;
  },

  // ---- CHAT (SSE stream) ----
  // Real call into the FastAPI proxy. Streams SSE: text deltas, skill
  // events, activity entries, and a final done event. Maps server events
  // onto the UI's StreamEvent shape so callers don't need to know the
  // wire format.
  async chat(opts: {
    threadId: string;
    userText: string;
    attachments?: ParsedAttachment[];
    onEvent?: StreamEventHandler;
    clarifierAnswers?: Record<string, string>;
    /** System-prompt addendum derived from the workspace's active
     * briefing template. Lets the agent emit cd_brief / brief
     * structured to match tenant-defined sections. */
    templatePrompt?: string;
    /** Reference image IDs (Tigris-stored, uploaded via uploadRefs).
     * The backend re-mints fresh presigned URLs at agent-spawn time so
     * Claude / Luma get fresh URLs every turn. */
    refIds?: string[];
    /** Demo toggle: when false, the agent's allowed_tools omits
     * graph_query / graph_schema for this turn. Useful against
     * tenants whose brand graph is empty so the LLM doesn't waste
     * time hitting it. Default true. */
    useBrandGraph?: boolean;
    /** Code-level enforcement: when set, generate_images uses
     * THIS list as Luma's image_ref array regardless of what the
     * LLM-composed tool call says. Set by the catalog's "Generate
     * this look" button so the look's exact hero + product refs
     * reach Luma deterministically. Don't set for multi-look gen
     * (each generate_images call needs its own subset). */
    enforcedImageRefs?: string[];
  }): Promise<{
    message: Message;
    skills: string[];
    receipts: Receipt[];
    clarifier: Clarifier | null;
    artifacts: Artifact[];
  }> {
    const { threadId, userText, attachments = [], onEvent } = opts;

    // The store appends a user message JUST before calling chat(). For a
    // regular send that's the typed text; for a clarifier-answers submit
    // it's the answers summary. Treat that latest user message as the
    // current turn — its content is more authoritative than `userText`,
    // which goes stale across the clarifier round-trip.
    const allMessages = await api.listMessages(threadId);
    const last = allMessages[allMessages.length - 1];
    const lastIsCurrentUser = last && last.role === "user";

    let message: string;
    if (lastIsCurrentUser) {
      const c = last.content;
      message = typeof c === "string" ? c : "";
    } else {
      message = (userText || "").trim();
    }
    if (!message && attachments.length) {
      message =
        "I've attached files. Take a look and tell me what stands out, then ask me what I'm working on.";
    }
    if (!message) message = "Hello.";

    const priorRaw = lastIsCurrentUser
      ? allMessages.slice(0, -1)
      : allMessages;
    const prior = priorRaw.map((m) => ({
      role: m.role,
      content: [
        {
          type: "text",
          text:
            typeof m.content === "string" ? m.content : "...",
        },
      ],
    }));

    const fd = new FormData();
    fd.append("message", message);
    fd.append("history", JSON.stringify(prior));
    if (threadId) fd.append("thread_id", threadId);
    if (opts.templatePrompt) {
      // Server reads this and prepends it to the CD agent's system
      // prompt so emit_cd_brief / emit_creative_brief follow the
      // tenant's section structure.
      fd.append("template_prompt", opts.templatePrompt);
    }
    if (opts.refIds && opts.refIds.length > 0) {
      fd.append("ref_ids", JSON.stringify(opts.refIds));
    }
    // Demo toggle. Default behavior (graph on) is implicit when the
    // field is omitted; we only send when explicitly disabled so old
    // backends don't break.
    if (opts.useBrandGraph === false) {
      fd.append("use_brand_graph", "false");
    }
    if (opts.enforcedImageRefs && opts.enforcedImageRefs.length > 0) {
      fd.append(
        "enforced_image_refs",
        JSON.stringify(opts.enforcedImageRefs)
      );
    }
    for (const att of attachments) {
      const f = fileCache.get(att.id);
      if (f) fd.append("files", f, f.name);
    }

    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(
        `Proxy ${res.status}: ${txt.slice(0, 200) || res.statusText}`
      );
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let partial = "";
    const skills: string[] = [];
    const receipts: Receipt[] = [];
    const artifacts: Artifact[] = [];
    let clarifier: Clarifier | null = null;
    let todos: TodoItem[] = [];
    const steps: StreamStep[] = [];
    let currentStep: StreamStep | null = null;
    let plan: ResearchPlan | null = null;
    const activity: ActivityEntry[] = [];
    const activityByToolUseId: Record<
      string,
      Extract<ActivityEntry, { kind: "tool" }>
    > = {};

    const emit = (ev: StreamEvent) => onEvent?.(ev);

    // Interleaved timeline of text + activity in arrival order. Drives the
    // segmented chat rendering so reasoning between tool calls is preserved
    // as separate bubbles instead of being concatenated into one block.
    let segments: TimelineSegment[] = [];
    const emitSegments = () => emit({ type: "segments", segments });

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() ?? "";
      for (const block of blocks) {
        const lines = block.split("\n");
        const evtLine = lines.find((l) => l.startsWith("event:"));
        const dataLine = lines.find((l) => l.startsWith("data:"));
        if (!evtLine || !dataLine) continue;
        const evt = evtLine.slice(6).trim();
        let data: any;
        try {
          data = JSON.parse(dataLine.slice(5).trim());
        } catch {
          continue;
        }

        if (evt === "step.start") {
          currentStep = {
            id: data.iteration,
            text: "",
            tools: [],
            done: false,
            isFinal: false,
          };
          steps.push(currentStep);
          emit({ type: "step.start", step: currentStep });
        } else if (evt === "step.end") {
          if (currentStep) {
            currentStep.done = true;
            currentStep.isFinal = !!data.is_final;
            emit({ type: "step.end", step: currentStep });
          }
        } else if (evt === "text") {
          partial += data.text;
          if (currentStep) currentStep.text += data.text;
          segments = appendTextChunk(segments, data.text);
          emit({ type: "content.delta", text: partial });
          emit({
            type: "step.delta",
            stepId: currentStep ? currentStep.id : null,
            text: data.text,
          });
          emitSegments();
        } else if (evt === "skill") {
          if (currentStep) {
            currentStep.tools.push({
              slug: data.slug,
              name: data.name || data.slug,
              description: data.description || "",
            });
          }
          if (!skills.includes(data.slug)) {
            const idx = skills.length;
            skills.push(data.slug);
            emit({
              type: "skill.start",
              skill: data.slug,
              index: idx,
              total: idx + 1,
            });
            receipts.push({
              kind: "skill-card",
              id: "r_" + uid(),
              skill: data.slug,
              name: data.name || data.slug,
              description: data.description || "",
            });
          }
          if (data.tool_use_id) {
            const entry: Extract<ActivityEntry, { kind: "tool" }> = {
              kind: "tool",
              tool_use_id: data.tool_use_id,
              slug: data.slug || "",
              name: data.name || data.slug || "",
              agent_type: data.agent_type || null,
              description: data.description || "",
              result: null,
              result_size: null,
            };
            activity.push(entry);
            activityByToolUseId[data.tool_use_id] = entry;
            segments = pushActivity(segments, entry);
            emit({ type: "activity", entry });
            emitSegments();
          }
        } else if (evt === "tool.result") {
          const entry = data.tool_use_id
            ? activityByToolUseId[data.tool_use_id]
            : null;
          if (entry) {
            entry.result = data.result || "";
            entry.result_size =
              typeof data.result_size === "number"
                ? data.result_size
                : null;
            segments = updateActivity(segments, entry);
            emit({ type: "activity.update", entry });
            emitSegments();
          }
        } else if (evt === "subagent.start") {
          const entry: ActivityEntry = {
            kind: "subagent_start",
            agent_id: data.agent_id || null,
            agent_type: data.agent_type || null,
            parent_tool_use_id: data.parent_tool_use_id || null,
          };
          activity.push(entry);
          segments = pushActivity(segments, entry);
          emit({ type: "activity", entry });
          emitSegments();
        } else if (evt === "subagent.stop") {
          const entry: ActivityEntry = {
            kind: "subagent_stop",
            agent_id: data.agent_id || null,
            agent_type: data.agent_type || null,
            parent_tool_use_id: data.parent_tool_use_id || null,
          };
          activity.push(entry);
          segments = pushActivity(segments, entry);
          emit({ type: "activity", entry });
          emitSegments();
        } else if (evt === "todos") {
          todos = Array.isArray(data.todos) ? data.todos : [];
          emit({ type: "todos", todos });
        } else if (evt === "cd_brief") {
          const entry: ActivityEntry = {
            kind: "artifact",
            kind_subtype: "cd_brief",
            data: data.cd_brief || data,
          };
          activity.push(entry);
          segments = pushActivity(segments, entry);
          emit({ type: "activity", entry });
          emitSegments();
        } else if (evt === "ask_user_question") {
          const entry: ActivityEntry = {
            kind: "artifact",
            kind_subtype: "ask_user_question",
            data: {
              qid: data.qid,
              questions: Array.isArray(data.questions) ? data.questions : [],
              thread_id: data.thread_id || threadId,
            },
          };
          activity.push(entry);
          segments = pushActivity(segments, entry);
          emit({ type: "activity", entry });
          emitSegments();
        } else if (evt === "plan") {
          plan = (data.plan || data) as ResearchPlan;
          emit({ type: "plan", plan });
        } else if (evt === "clarifier") {
          clarifier = (data.clarifier || data) as Clarifier;
          emit({ type: "clarifier", clarifier });
        } else if (evt === "artifact") {
          const artifact = (data.artifact || data) as Artifact;
          artifacts.push(artifact);
          emit({ type: "artifact", artifact });
          // Renderable artifacts (images today; future inline cards) need
          // to land in the timeline too so the chat actually shows them.
          // brief / cd_brief artifacts surface via the separate briefing_record
          // → refetch path and don't need this. Without this branch the
          // ImageGalleryCard never mounts even though generate_images
          // emitted the artifact correctly.
          if (artifact.kind === "images") {
            const entry: ActivityEntry = {
              kind: "artifact",
              kind_subtype: "images",
              data: artifact as unknown,
            };
            activity.push(entry);
            segments = pushActivity(segments, entry);
            emit({ type: "activity", entry });
            emitSegments();
          } else if (artifact.kind === "product_catalog") {
            const entry: ActivityEntry = {
              kind: "artifact",
              kind_subtype: "product_catalog",
              data: artifact as unknown,
            };
            activity.push(entry);
            segments = pushActivity(segments, entry);
            emit({ type: "activity", entry });
            emitSegments();
          }
        } else if (evt === "critique") {
          const artifactId = data.artifact_id;
          const warnings = Array.isArray(data.warnings) ? data.warnings : [];
          const target = artifacts.find((a) => a.id === artifactId);
          if (target) {
            (target as any).critique = { warnings };
            emit({ type: "artifact.update", artifact: target });
          }
        } else if (evt === "briefing_record") {
          emit({
            type: "briefing_record",
            id: data.id,
            status: data.status,
          });
        } else if (evt === "image_gen_progress") {
          // slot_id is required by the type but defensively coerce —
          // an event without it is unroutable, so drop it.
          if (typeof data.slot_id !== "string" || !data.slot_id) {
            continue;
          }
          emit({
            type: "image_gen_progress",
            slot_id: data.slot_id,
            phase: data.phase,
            index: typeof data.index === "number" ? data.index : undefined,
            total: typeof data.total === "number" ? data.total : undefined,
            generation_id:
              typeof data.generation_id === "string"
                ? data.generation_id
                : undefined,
            prompt:
              typeof data.prompt === "string" ? data.prompt : undefined,
            aspect_ratio:
              typeof data.aspect_ratio === "string"
                ? data.aspect_ratio
                : undefined,
            url: typeof data.url === "string" ? data.url : undefined,
            error: typeof data.error === "string" ? data.error : undefined,
          });
        } else if (evt === "error") {
          throw new Error(
            (data && data.message) || "Proxy error"
          );
        }
        // 'start' and 'done' are informational — no UI mapping needed.
      }
    }

    if (skills.length > 0) {
      const lastIdx = skills.length - 1;
      emit({
        type: "skill.done",
        skill: skills[lastIdx],
        index: lastIdx,
        total: skills.length,
      });
    }

    if (artifacts.length) {
      await api.appendArtifacts(threadId, artifacts);
    }

    const assistantMsg = await api.appendMessage(threadId, {
      role: "assistant",
      content:
        partial ||
        (clarifier ? "Two quick things before I commit —" : ""),
      steps,
      plan,
      skills,
      receipts,
      activity,
      todos,
      segments,
      artifactIds: artifacts.map((a) => a.id),
      ...(clarifier ? { clarifier, awaitingClarifier: true } : {}),
    });
    emit({ type: "done", messageId: assistantMsg.id });

    return {
      message: assistantMsg,
      skills,
      receipts,
      clarifier,
      artifacts,
    };
  },

  // ---- ARTIFACTS ----
  async listArtifacts(threadId: string): Promise<Artifact[]> {
    await sleep(60);
    const raw = localStorage.getItem("inku.artifacts." + threadId);
    return raw ? (JSON.parse(raw) as Artifact[]) : [];
  },

  async appendArtifacts(
    threadId: string,
    artifacts: Artifact[]
  ): Promise<Artifact[]> {
    const cur = await api.listArtifacts(threadId);
    const next = [...cur, ...artifacts];
    localStorage.setItem("inku.artifacts." + threadId, JSON.stringify(next));
    return next;
  },

  async clearArtifacts(threadId: string): Promise<{ ok: true }> {
    localStorage.removeItem("inku.artifacts." + threadId);
    return { ok: true };
  },

  // ---- FILE UPLOAD / PARSE ----
  async uploadAndParse(file: File): Promise<ParsedAttachment> {
    await sleep(200);
    const name = file.name || "Untitled";
    const ext = (name.split(".").pop() || "").toLowerCase();
    const isCSV = ext === "csv" || ext === "xlsx";
    const result: ParsedAttachment = {
      id: "f_" + uid(),
      name,
      type: isCSV ? "csv" : "pdf",
      size: file.size || 0,
      pages: isCSV ? undefined : null,
      rows: isCSV ? null : undefined,
      uploadedAt: Date.now(),
      parsed: {
        title: name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "),
        summary: `${name} · ${(file.size / 1024).toFixed(1)}KB · ready to send to backend`,
        tags: [isCSV ? "csv" : "pdf"],
      },
    };
    fileCache.set(result.id, file);
    return result;
  },

  // ---- AGENTS ----
  async listAgents(): Promise<Agent[]> {
    await sleep(150);
    const raw = localStorage.getItem("inku.agents");
    if (raw) return JSON.parse(raw) as Agent[];
    localStorage.setItem("inku.agents", JSON.stringify(SEEDED_AGENTS));
    return SEEDED_AGENTS;
  },

  async createScheduledAgent(payload: {
    cadence?: string;
    title?: string;
    desc?: string;
  }): Promise<Agent> {
    await sleep(200);
    const list = await api.listAgents();
    const agent: Agent = {
      id: "agent_" + uid(),
      cadence: payload.cadence ?? "Every Monday · 09:00",
      title: payload.title ?? "New agent",
      desc: payload.desc || "User-scheduled agent.",
      status: "scheduled",
      lastRun: "Never",
    };
    localStorage.setItem("inku.agents", JSON.stringify([...list, agent]));
    return agent;
  },

  async toggleAgent(id: string): Promise<Agent | undefined> {
    const list = await api.listAgents();
    const next = list.map((a) =>
      a.id === id
        ? {
            ...a,
            status:
              a.status === "paused"
                ? ("scheduled" as const)
                : ("paused" as const),
          }
        : a
    );
    localStorage.setItem("inku.agents", JSON.stringify(next));
    return next.find((a) => a.id === id);
  },

  async deleteAgent(id: string): Promise<{ ok: true }> {
    const list = await api.listAgents();
    localStorage.setItem(
      "inku.agents",
      JSON.stringify(list.filter((a) => a.id !== id))
    );
    return { ok: true };
  },

  // ---- AUTH ----
  // Probe a gated endpoint with the currently-stored credentials. Used
  // by the SPA on boot to decide whether to render LoginPage or the app.
  async authProbe(): Promise<boolean> {
    try {
      const res = await fetch(API_BASE + "/briefings", {
        method: "HEAD",
        headers: authHeaders(),
      });
      // FastAPI doesn't auto-handle HEAD on @router.get — fall back to
      // a tiny GET if HEAD returns 405.
      if (res.status === 405) {
        const r2 = await fetch(API_BASE + "/briefings?status=__probe__", {
          headers: authHeaders(),
        });
        return r2.status !== 401;
      }
      return res.status !== 401;
    } catch {
      return false;
    }
  },

  // Validate username/password against the gated endpoint, persist
  // base64(user:pass) to localStorage on success, return ok flag.
  async signIn(
    username: string,
    password: string
  ): Promise<{ ok: boolean; status: number }> {
    const encoded = btoa(`${username}:${password}`);
    try {
      const res = await fetch(API_BASE + "/briefings?status=__probe__", {
        headers: { Authorization: `Basic ${encoded}` },
      });
      if (res.ok) {
        localStorage.setItem(AUTH_STORAGE_KEY, encoded);
        return { ok: true, status: res.status };
      }
      return { ok: false, status: res.status };
    } catch {
      return { ok: false, status: 0 };
    }
  },

  signOut(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  },

  // ─── Brand Graph Builder ──────────────────────────────────────────
  // Mirrors server/graph/endpoint.py. tenant_id defaults to "100" on
  // the server side; the page passes a fresh one only when testing.

  graph: {
    async listTenants(): Promise<{
      tenants: Array<{
        tenant_id: string;
        name: string | null;
        created_at: string | null;
        updated_at: string | null;
        node_count: number;
      }>;
      count: number;
    }> {
      const res = await fetch(`${API_BASE}/graph/tenants`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`/graph/tenants ${res.status}`);
      return res.json();
    },

    async getTree(opts: { tenantId?: string; includeHistory?: boolean } = {}): Promise<GraphSnapshot> {
      const params = new URLSearchParams();
      if (opts.tenantId) params.set("tenant_id", opts.tenantId);
      if (opts.includeHistory) params.set("include_history", "true");
      const url = `${API_BASE}/graph${params.toString() ? "?" + params.toString() : ""}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`/graph ${res.status}: ${txt.slice(0, 200)}`);
      }
      return res.json() as Promise<GraphSnapshot>;
    },

    async getHistory(
      opts: { tenantId?: string; limit?: number } = {}
    ): Promise<{ edits: GraphEditEntry[]; count: number; tenant_id: string }> {
      const params = new URLSearchParams();
      if (opts.tenantId) params.set("tenant_id", opts.tenantId);
      if (opts.limit) params.set("limit", String(opts.limit));
      const res = await fetch(
        `${API_BASE}/graph/history${params.toString() ? "?" + params.toString() : ""}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`/graph/history ${res.status}`);
      return res.json() as Promise<{
        edits: GraphEditEntry[];
        count: number;
        tenant_id: string;
      }>;
    },

    async listProposals(
      opts: { tenantId?: string } = {}
    ): Promise<{ proposals: GraphProposal[]; count: number; tenant_id: string }> {
      const params = new URLSearchParams();
      if (opts.tenantId) params.set("tenant_id", opts.tenantId);
      const res = await fetch(
        `${API_BASE}/graph/proposals${params.toString() ? "?" + params.toString() : ""}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`/graph/proposals ${res.status}`);
      return res.json() as Promise<{
        proposals: GraphProposal[];
        count: number;
        tenant_id: string;
      }>;
    },

    async getProposal(
      proposalId: string,
      opts: { tenantId?: string } = {}
    ): Promise<GraphProposal> {
      const params = new URLSearchParams();
      if (opts.tenantId) params.set("tenant_id", opts.tenantId);
      const res = await fetch(
        `${API_BASE}/graph/proposals/${encodeURIComponent(proposalId)}${
          params.toString() ? "?" + params.toString() : ""
        }`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`/graph/proposals/${proposalId} ${res.status}`);
      return res.json() as Promise<GraphProposal>;
    },

    async applyProposal(
      proposalId: string,
      opts: {
        tenantId?: string;
        selectedIndices?: number[];
        /** new_node_type proposals only: false = register the type
         *  without creating initial_instances ("Add type only" UI). */
        applyInstances?: boolean;
        /** flag_contradiction proposals only: required at apply time.
         * One of "keep_both" / "delete_a" / "delete_b" — what the
         * user picked on the ContradictionCard. */
        resolution?: "keep_both" | "delete_a" | "delete_b";
      } = {}
    ): Promise<{ affected_node_ids: string[]; edit_id: string; applied_at: string }> {
      const body: Record<string, unknown> = {};
      if (opts.tenantId) body.tenant_id = opts.tenantId;
      if (opts.selectedIndices) body.selected_indices = opts.selectedIndices;
      if (opts.applyInstances !== undefined) body.apply_instances = opts.applyInstances;
      if (opts.resolution) body.resolution = opts.resolution;
      const res = await fetch(
        `${API_BASE}/graph/proposals/${encodeURIComponent(proposalId)}/apply`,
        {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`apply ${res.status}: ${txt.slice(0, 200) || res.statusText}`);
      }
      return res.json();
    },

    async rejectProposal(
      proposalId: string,
      opts: { tenantId?: string; userReason?: string } = {}
    ): Promise<{ rejected_at: string; user_reason: string; edit_id: string }> {
      const body: Record<string, unknown> = {};
      if (opts.tenantId) body.tenant_id = opts.tenantId;
      if (opts.userReason) body.user_reason = opts.userReason;
      const res = await fetch(
        `${API_BASE}/graph/proposals/${encodeURIComponent(proposalId)}/reject`,
        {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`reject ${res.status}: ${txt.slice(0, 200) || res.statusText}`);
      }
      return res.json();
    },

    /**
     * Stream a turn through the graph_builder agent. Mirrors api.chat
     * but simpler: the /graph/chat SSE event set is small (start, text,
     * tool_use, artifact, done, error).
     *
     * Returns a Promise that resolves when the stream completes (either
     * a `done` event lands or the connection closes). All events flow
     * through onEvent.
     */
    async chat(opts: {
      sessionId: string;
      tenantId?: string;
      message: string;
      refIds?: string[];
      sessionMode?: "onboarding" | "living" | "unspecified";
      onEvent: GraphStreamEventHandler;
    }): Promise<void> {
      const fd = new FormData();
      fd.append("message", opts.message);
      fd.append("session_id", opts.sessionId);
      if (opts.tenantId) fd.append("tenant_id", opts.tenantId);
      if (opts.refIds && opts.refIds.length > 0) {
        fd.append("ref_ids", JSON.stringify(opts.refIds));
      }
      if (opts.sessionMode) fd.append("session_mode", opts.sessionMode);

      const res = await fetch(`${API_BASE}/graph/chat`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(
          `/graph/chat ${res.status}: ${txt.slice(0, 200) || res.statusText}`
        );
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // SSE frame parser. Frames are separated by \n\n; each frame has
      // `event: <name>` + `data: <json>` lines. Mirror the same parser
      // the existing chat() uses but with a simpler event set.
      const flushFrame = (frame: string) => {
        let eventName = "message";
        const dataLines: string[] = [];
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trim());
          }
        }
        const raw = dataLines.join("\n");
        if (!raw) return;
        let parsed: Record<string, unknown> = {};
        try {
          parsed = JSON.parse(raw);
        } catch {
          // Ignore malformed frames — server should never emit them.
          return;
        }
        switch (eventName) {
          case "start":
            opts.onEvent({
              type: "start",
              model: String(parsed.model || ""),
              mode: String(parsed.mode || ""),
            });
            break;
          case "text":
            opts.onEvent({ type: "text", text: String(parsed.text || "") });
            break;
          case "tool_use":
            opts.onEvent({
              type: "tool_use",
              tool: String(parsed.tool || ""),
              input: (parsed.input as Record<string, unknown>) || {},
            });
            break;
          case "artifact": {
            const artifact = parsed.artifact as
              | {
                  kind?: string;
                  proposal?: GraphProposal;
                  id?: string;
                  node_id?: string;
                  node_category?: string;
                  node_display_label?: string;
                  node_content?: string;
                  generation_id?: string;
                  image_url?: string;
                  prompt?: string;
                  aspect_ratio?: string;
                  ref_ids?: string[];
                }
              | undefined;
            if (!artifact) break;
            if (artifact.kind === "graph_proposal" && artifact.proposal) {
              opts.onEvent({ type: "artifact", proposal: artifact.proposal });
            } else if (artifact.kind === "verification" && artifact.image_url) {
              const aRec = artifact as Record<string, unknown>;
              opts.onEvent({
                type: "verification",
                verification: {
                  id: String(artifact.id || ""),
                  node_id: String(artifact.node_id || ""),
                  node_category: String(artifact.node_category || ""),
                  node_display_label: String(artifact.node_display_label || ""),
                  node_content: String(artifact.node_content || ""),
                  generation_id: String(artifact.generation_id || ""),
                  image_url: String(artifact.image_url),
                  prompt: String(artifact.prompt || ""),
                  composed_prompt:
                    typeof aRec.composed_prompt === "string"
                      ? aRec.composed_prompt
                      : undefined,
                  aspect_ratio: String(artifact.aspect_ratio || "3:2"),
                  ref_ids: Array.isArray(artifact.ref_ids) ? artifact.ref_ids : [],
                  primary_node_id:
                    typeof aRec.primary_node_id === "string"
                      ? aRec.primary_node_id
                      : null,
                  contributing_node_ids: Array.isArray(aRec.contributing_node_ids)
                    ? (aRec.contributing_node_ids as string[])
                    : [],
                  breakdown:
                    (aRec.breakdown && typeof aRec.breakdown === "object")
                      ? (aRec.breakdown as Record<string, number>)
                      : {},
                },
              });
            }
            break;
          }
          case "verification_progress": {
            opts.onEvent({
              type: "verification_progress",
              slot_id: String(parsed.slot_id || ""),
              node_id: String(parsed.node_id || ""),
              phase: String(parsed.phase || ""),
              generation_id: parsed.generation_id ? String(parsed.generation_id) : undefined,
              prompt: parsed.prompt ? String(parsed.prompt) : undefined,
              aspect_ratio: parsed.aspect_ratio ? String(parsed.aspect_ratio) : undefined,
              error: parsed.error ? String(parsed.error) : undefined,
            });
            break;
          }
          case "ask_user_question": {
            const qs = parsed.questions as
              | Array<{
                  id?: string;
                  question: string;
                  options?: Array<string | { label: string; description?: string }>;
                  multi?: boolean;
                }>
              | undefined;
            if (qs && Array.isArray(qs)) {
              opts.onEvent({
                type: "ask_user_question",
                qid: String(parsed.qid || ""),
                thread_id: String(parsed.thread_id || ""),
                questions: qs,
              });
            }
            break;
          }
          case "done":
            opts.onEvent({
              type: "done",
              session_id: (parsed.session_id as string | null) ?? null,
            });
            break;
          case "error":
            opts.onEvent({
              type: "error",
              message: String(parsed.message || "Error"),
              detail: parsed.detail ? String(parsed.detail) : undefined,
            });
            break;
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        while (true) {
          const idx = buffer.indexOf("\n\n");
          if (idx < 0) break;
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          flushFrame(frame);
        }
      }
      // Flush trailing frame if the stream closed without a final \n\n.
      if (buffer.trim()) flushFrame(buffer);
    },

    async resetSession(sessionId: string): Promise<{ reset: number }> {
      const res = await fetch(`${API_BASE}/graph/chat/reset`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`reset ${res.status}`);
      return res.json();
    },
  },
};

export type Api = typeof api;
